import type { ConnectionMode, DiscoveryProvider } from "@coremonit/types";
import type { WizardData } from "../wizardStore";
import { OptionGrid, StepHeader, Field } from "./common";
import { Input } from "@coremonit/design-system";
import { Plug, Bot, Search, Upload } from "lucide-react";

const MODES: Array<{ value: ConnectionMode; label: string; description: string }> = [
  { value: "api",               label: "API",               description: "Poll a metrics/log/trace endpoint the service exposes." },
  { value: "agent",             label: "Agent",             description: "Push from a sidecar agent (recommended for k8s)." },
  { value: "service-discovery", label: "Service Discovery", description: "Discover the service via a registry (Consul, Eureka, k8s…)." },
  { value: "import",            label: "Import",            description: "Upload existing config (CSV, JSON, Swagger, OpenAPI…)." },
];

const DISCOVERY: Array<{ value: DiscoveryProvider; label: string }> = [
  { value: "kubernetes-api", label: "Kubernetes API" },
  { value: "docker-api",     label: "Docker API" },
  { value: "consul",         label: "Consul" },
  { value: "eureka",         label: "Eureka" },
  { value: "istio",          label: "Istio" },
  { value: "linkerd",        label: "Linkerd" },
  { value: "aws-ecs",        label: "AWS ECS" },
  { value: "azure-aks",      label: "Azure AKS" },
  { value: "google-gke",     label: "Google GKE" },
  { value: "manual-csv",     label: "Manual · CSV" },
  { value: "manual-json",    label: "Manual · JSON" },
  { value: "swagger",        label: "Swagger" },
  { value: "openapi",        label: "OpenAPI" },
  { value: "graphql",        label: "GraphQL" },
];

interface Props { data: WizardData; update: (p: Partial<WizardData>) => void }

export function Step4Connection({ data, update }: Props) {
  const iconFor = (v: ConnectionMode) => v === "api" ? <Plug size={14} /> : v === "agent" ? <Bot size={14} /> : v === "service-discovery" ? <Search size={14} /> : <Upload size={14} />;
  return (
    <div>
      <StepHeader title="Connection" description="How should CoreMonit reach this service to ingest telemetry?" />
      <OptionGrid<ConnectionMode>
        value={data.connectionMode}
        onChange={(v) => update({ connectionMode: v })}
        options={MODES.map(m => ({ ...m, icon: iconFor(m.value) }))}
        cols={2}
      />
      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {(data.connectionMode === "api" || data.connectionMode === "agent") && (
          <Field label="Endpoint">
            <Input value={data.connectionEndpoint ?? ""} onChange={(v) => update({ connectionEndpoint: v })}
                   placeholder={data.connectionMode === "api" ? "https://svc/metrics" : "http://otel-collector.observability:4318"} size="md" />
          </Field>
        )}
        {data.connectionMode === "service-discovery" && (
          <Field label="Discovery provider">
            <select
              value={data.discovery ?? ""}
              onChange={(e) => update({ discovery: e.target.value as DiscoveryProvider })}
              style={{ width: "100%", padding: "10px 12px", background: "#0f1a2e", border: `1px solid rgba(45,127,249,0.15)`, borderRadius: 12, color: "#e8edf5", fontSize: 13 }}
            >
              {DISCOVERY.filter(d => !d.value.startsWith("manual") && !["swagger", "openapi", "graphql"].includes(d.value)).map(d => (
                <option key={d.value} value={d.value} style={{ background: "#0c1424" }}>{d.label}</option>
              ))}
            </select>
          </Field>
        )}
        {data.connectionMode === "import" && (
          <Field label="Import format">
            <select
              value={data.discovery ?? "manual-csv"}
              onChange={(e) => update({ discovery: e.target.value as DiscoveryProvider })}
              style={{ width: "100%", padding: "10px 12px", background: "#0f1a2e", border: `1px solid rgba(45,127,249,0.15)`, borderRadius: 12, color: "#e8edf5", fontSize: 13 }}
            >
              {DISCOVERY.filter(d => d.value.startsWith("manual") || ["swagger", "openapi", "graphql"].includes(d.value)).map(d => (
                <option key={d.value} value={d.value} style={{ background: "#0c1424" }}>{d.label}</option>
              ))}
            </select>
          </Field>
        )}
      </div>
    </div>
  );
}
